import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, SETUP_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { problemFieldErrors, problemMessage } from "@/api/client";
import { useJobCodes } from "@/hooks/useJobCodes";
import type { JobCode, JobCodeKind } from "@/api/types";
import { DialogShell } from "@/components/DialogShell";
import "./JobCodes.css";

/** Mirrors `JobCodeEndpoints.AllowedKinds` — option VALUES are the lowercase strings the
 *  API's CHECK constraint and `JobCodeKind` (api/types.ts) both use; labels are what a
 *  farmer reads. DELIBERATELY a different list from `Places.tsx`'s `KIND_OPTIONS` — see
 *  db/migrations/0027_job_code.sql's header for why the two vocabularies diverge. */
const KIND_OPTIONS: { value: JobCodeKind; label: string }[] = [
  { value: "field", label: "Field" },
  { value: "livestock", label: "Livestock" },
  { value: "shop", label: "Shop" },
  { value: "equipment", label: "Equipment" },
  { value: "general", label: "General" },
  { value: "other", label: "Other" },
];

function kindLabel(kind: JobCodeKind): string {
  return KIND_OPTIONS.find((k) => k.value === kind)?.label ?? kind;
}

interface JobCodeFormState {
  code: string;
  name: string;
  kind: JobCodeKind;
}

function blankForm(): JobCodeFormState {
  return { code: "", name: "", kind: "general" };
}

function formFromJobCode(jobCode: JobCode): JobCodeFormState {
  return { code: jobCode.code, name: jobCode.name, kind: jobCode.kind };
}

/**
 * ef-023 — the farmer keeps their own vocabulary for WHAT a day's hours were for,
 * distinct from `Places.tsx`'s WHERE. `Payroll.tsx`'s "More payroll screens" nav links
 * here, next to Saved places; this is a payroll sub-screen, not a nav destination of its
 * own — see `App.tsx`'s routing note.
 *
 * SAME SHARED ADD/EDIT FORM AS `Places.tsx`, same reasoning: one set of fields, re-seeded
 * from whichever record is being worked on, with the submit label naming the mode.
 *
 * ARCHIVING KEEPS THE CODE ON ALREADY-RECORDED HOURS — the opposite invariant from
 * `Places.tsx`'s own archive copy. A punch's resolved place is looked up LIVE against the
 * tenant's currently-active places, so archiving one falls every punch that used it back
 * to a bare coordinate; a job-code allocation is the opposite shape — it is a value
 * `time_entry_allocation` stores directly on the row at the moment it's saved
 * (`JobCodeContracts.cs`'s `AllocationResponse`), never re-resolved, so archiving the code
 * changes nothing about hours already split against it. The confirm dialog below says so.
 */
export function JobCodes() {
  const { active, archived, status, error, create, update, archive } = useJobCodes();

  const [form, setForm] = useState<JobCodeFormState>(blankForm());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archiving, setArchiving] = useState<JobCode | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const codeId = useId();
  const nameId = useId();
  const kindId = useId();

  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const archiveTriggerRef = useRef<HTMLButtonElement | null>(null);

  function startEdit(jobCode: JobCode): void {
    setEditingId(jobCode.id);
    setForm(formFromJobCode(jobCode));
    setFormError(null);
    setFieldErrors(null);
  }

  function cancelEdit(): void {
    setEditingId(null);
    setForm(blankForm());
    setFormError(null);
    setFieldErrors(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);

    const trimmedCode = form.code.trim();
    const trimmedName = form.name.trim();

    /*
     * @ui-check: client-side ONLY, and required-ness only — the server's own
     * field-by-field checks (JobCodeEndpoints.ValidateFields: length bounds on both
     * code and name, kind membership) still run after this and remain the actual
     * contract enforcement. This guard exists for the one case a missing value would
     * otherwise reach the network as an empty string rather than a validation error.
     */
    const missing: Record<string, string[]> = {};
    if (trimmedCode === "") missing.code = ["A code is required."];
    if (trimmedName === "") missing.name = ["A name is required."];
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setFormError(Object.values(missing).flat()[0] ?? null);
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await update(editingId, { code: trimmedCode, name: trimmedName, kind: form.kind });
      } else {
        await create({ code: trimmedCode, name: trimmedName, kind: form.kind });
      }
      cancelEdit();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not save this job code."));
    } finally {
      setSaving(false);
    }
  }

  function openArchiveConfirm(jobCode: JobCode, trigger: HTMLButtonElement | null): void {
    archiveTriggerRef.current = trigger;
    setArchiving(jobCode);
    setArchiveError(null);
  }

  function closeArchiveConfirm(): void {
    setArchiving(null);
    setArchiveError(null);
    archiveTriggerRef.current?.focus();
  }

  async function confirmArchive(): Promise<void> {
    if (!archiving) return;
    setArchiveBusy(true);
    setArchiveError(null);
    try {
      const archivedId = archiving.id;
      await archive(archivedId);
      setArchiving(null);
      // Editing the very row being archived would submit an update against a job code
      // that no longer exists active — leave the shared form in add mode instead.
      if (editingId === archivedId) cancelEdit();
    } catch (err) {
      setArchiveError(problemMessage(err, "Could not archive this job code."));
    } finally {
      setArchiveBusy(false);
    }
  }

  const activeCount = active?.length ?? 0;

  return (
    <main className="job-codes-page">
      {/* The back link is gone (ADR 0009 §2: "No back buttons") — Job codes is now a
          Setup tab, reached and left via the area tab strip below. */}
      <PageHeader
        title="Job codes"
        subtitle="Name what a day's hours were for, so the queue can split a row across the work it covered."
      />

      <AreaTabs area="/setup" tabs={SETUP_TABS} label="Setup" />

      <div className="job-codes-page__grid">
        <section className="job-codes-page__form-card" aria-labelledby="job-codes-form-heading">
          <h2 id="job-codes-form-heading" className="job-codes-page__card-title">
            {editingId ? "Edit job code" : "Add a job code"}
          </h2>

          <form className="job-codes-page__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
            <div className="job-codes-page__field">
              <label htmlFor={codeId}>Code</label>
              <input
                id={codeId}
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                aria-invalid={fieldErrors?.code ? true : undefined}
              />
              {fieldErrors?.code && (
                <p className="job-codes-page__field-error" role="alert">
                  {fieldErrors.code[0]}
                </p>
              )}
            </div>

            <div className="job-codes-page__field">
              <label htmlFor={nameId}>Name</label>
              <input
                id={nameId}
                type="text"
                value={form.name}
                maxLength={80}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                aria-invalid={fieldErrors?.name ? true : undefined}
              />
              {fieldErrors?.name && (
                <p className="job-codes-page__field-error" role="alert">
                  {fieldErrors.name[0]}
                </p>
              )}
            </div>

            <div className="job-codes-page__field">
              <label htmlFor={kindId}>Kind</label>
              <select
                id={kindId}
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as JobCodeKind })}
              >
                {KIND_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* The general refusal — a 409 code conflict, or any server rejection that is
                not about one specific field above. Suppressed once field-level errors are
                showing, so a farmer never reads the same rejection twice. */}
            {formError && !fieldErrors && (
              <p className="job-codes-page__form-error" role="alert">
                {formError}
              </p>
            )}

            <div className="job-codes-page__form-actions">
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? "Saving…" : editingId ? "Save changes" : "Save job code"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={cancelEdit}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        {/* data-state is the tester's ground truth for this section (ui-breadcrumbs
            §1): loading / error / empty / ready, read off the DOM rather than inferred
            from whether some sentence happens to be on screen. */}
        <section
          className="job-codes-page__list-card"
          aria-labelledby="job-codes-heading"
          data-state={status === "ready" && activeCount === 0 ? "empty" : status}
        >
          <h2 id="job-codes-heading" className="job-codes-page__card-title">
            Your job codes
          </h2>

          {status === "loading" && <StateMessage tone="loading" title="Loading job codes…" />}

          {status === "error" && (
            <StateMessage tone="error" title="Could not load job codes." detail={error ?? undefined} />
          )}

          {status === "ready" && activeCount === 0 && (
            <StateMessage
              tone="empty"
              title="No job codes yet."
              detail="Add the field work, shop time and equipment jobs your team logs against, and the approval queue will be able to show how a day split across them."
            />
          )}

          {status === "ready" && active !== null && activeCount > 0 && (
            <ul className="job-codes-page__list" aria-labelledby="job-codes-heading">
              {active.map((jobCode) => (
                <li key={jobCode.id} className="job-codes-page__row">
                  <span className="job-codes-page__meta">
                    <span className="job-codes-page__code">{jobCode.code}</span>
                    <span className="job-codes-page__name">{jobCode.name}</span>
                    <span className="job-codes-page__kind">{kindLabel(jobCode.kind)}</span>
                  </span>
                  <span className="job-codes-page__row-actions">
                    <button type="button" className="btn btn--ghost" onClick={() => startEdit(jobCode)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={(e) => openArchiveConfirm(jobCode, e.currentTarget)}
                    >
                      Archive
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            className="btn btn--ghost job-codes-page__archived-toggle"
            aria-expanded={archivedOpen}
            onClick={() => setArchivedOpen((v) => !v)}
          >
            {archivedOpen ? "Hide" : "Show"} archived job codes
            {archived.length > 0 ? ` (${archived.length})` : ""}
          </button>

          {/* A <fieldset>/<legend> pair, not a styled <div> — same reasoning as
              Places.tsx's identical disclosure: the legend gives the group a real
              accessible NAME without a second visible heading repeating the toggle
              button's own label. */}
          {archivedOpen && (
            <fieldset className="job-codes-page__archived">
              <legend className="visually-hidden">Archived job codes</legend>
              {archived.length === 0 ? (
                <p className="job-codes-page__archived-empty">Nothing archived yet.</p>
              ) : (
                <ul className="job-codes-page__list job-codes-page__list--archived">
                  {archived.map((jobCode) => (
                    <li key={jobCode.id} className="job-codes-page__row job-codes-page__row--archived">
                      <span className="job-codes-page__meta">
                        <span className="job-codes-page__code">{jobCode.code}</span>
                        <span className="job-codes-page__name">{jobCode.name}</span>
                        <span className="job-codes-page__kind">{kindLabel(jobCode.kind)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </fieldset>
          )}
        </section>
      </div>

      {archiving && (
        <DialogShell
          title={<>Archive job code {archiving.code}?</>}
          titleId="archive-job-code-title"
          role="alertdialog"
          size="sm"
          onClose={closeArchiveConfirm}
          busy={archiveBusy}
          initialFocus={cancelBtnRef}
          describedBy="archive-job-code-desc"
          footer={
            <>
              <button
                ref={cancelBtnRef}
                type="button"
                className="btn btn--ghost"
                onClick={closeArchiveConfirm}
                disabled={archiveBusy}
              >
                Cancel
              </button>
              <button
                ref={confirmBtnRef}
                type="button"
                className="btn btn--danger"
                onClick={() => void confirmArchive()}
                disabled={archiveBusy}
              >
                {archiveBusy ? "Archiving…" : "Archive"}
              </button>
            </>
          }
        >
          {/* THE OPPOSITE INVARIANT FROM Places.tsx's own confirm copy, deliberately —
              see this component's own header comment. Any hours already split against
              this code keep it; nothing that already happened changes. */}
          <p id="archive-job-code-desc" className="job-codes-page__dialog-desc">
            Any hours already allocated to {archiving.code} keep this code — allocations
            are stored on the entry itself at the moment they are saved, so nothing that
            already happened changes. It moves to your archived list and won&apos;t be
            offered when allocating new hours. There is no way to bring it back; adding
            the same code again would be a new job code.
          </p>
          {archiveError && (
            <p className="job-codes-page__dialog-error" role="alert">
              {archiveError}
            </p>
          )}
        </DialogShell>
      )}
    </main>
  );
}
